import React, { useEffect, useState } from "react";
import { personalDetails } from "../Details";

function Home() {
  // Destructure these values carefully, providing fallbacks if needed
  const { name = "Robert O'Connor", img } = personalDetails;

  // Define taglines directly in the component to ensure they exist
  const taglines = [
    "Technical Lead",
    "Solution Architect",
    "Cloud Engineer",
    "Problem Solver",
    "Technologist",
  ];

  const [currentTaglineIndex, setCurrentTaglineIndex] = useState(0);
  const [initialAnimationComplete, setInitialAnimationComplete] =
    useState(false);

  // Use a single state for the complete tagline
  const [greetingText, setGreetingText] = useState("");
  const [introText, setIntroText] = useState("");
  const [showName, setShowName] = useState(false);
  const [showImA, setShowImA] = useState(false);
  const [showTagline, setShowTagline] = useState(false);
  const [blurKey, setBlurKey] = useState(0);
  const [glitchKey, setGlitchKey] = useState(0);
  const [firstCycleComplete, setFirstCycleComplete] = useState(false);

  // Initial animations
  useEffect(() => {
    // Hard-code exact greeting text to ensure it displays correctly
    const animateGreeting = () => {
      let index = 0;
      const helloText = "Hi there,";

      const greetingInterval = setInterval(() => {
        if (index < helloText.length) {
          setGreetingText(helloText.substring(0, index + 1));
          index++;
        } else {
          clearInterval(greetingInterval);
          // Add wave emoji separately after a short delay
          setTimeout(() => {
            setGreetingText("Hi there, 👋");
            // Move to next animation
            animateIntro();
          }, 300);
        }
      }, 100);
    };

    const animateIntro = () => {
      let index = 0;
      const myNameIs = "my name is";

      const introInterval = setInterval(() => {
        if (index < myNameIs.length) {
          setIntroText(myNameIs.substring(0, index + 1));
          index++;
        } else {
          clearInterval(introInterval);
          // Move to name animation
          animateName();
        }
      }, 100);
    };

    const animateName = () => {
      // Show name with fade-in sparkle effect
      setShowName(true);
      // Wait for the animation to complete before moving to tagline
      setTimeout(() => {
        animateFullTagline();
      }, 1200);
    };

    const animateFullTagline = () => {
      // First fade in "I'm a"
      setShowImA(true);
      // Then after a short delay, blur morph the first tagline
      setTimeout(() => {
        setShowTagline(true);
        setBlurKey(1);
        setInitialAnimationComplete(true);
      }, 600);
    };

    // Start the animation sequence
    animateGreeting();
  }, []);

  // Tagline cycling - blur morph for first cycle, then glitch
  useEffect(() => {
    if (!initialAnimationComplete) return;

    const taglineInterval = setInterval(
      () => {
        const nextIndex = (currentTaglineIndex + 1) % taglines.length;

        // Check if we're about to complete the first cycle
        if (nextIndex === 0 && !firstCycleComplete) {
          setFirstCycleComplete(true);
        }

        setCurrentTaglineIndex(nextIndex);

        if (firstCycleComplete) {
          // Use glitch effect after first cycle
          setGlitchKey((prev) => prev + 1);
        } else {
          // Use blur morph effect during first cycle
          setBlurKey((prev) => prev + 1);
        }
      },
      firstCycleComplete ? 2500 : 1300,
    );

    return () => clearInterval(taglineInterval);
  }, [
    initialAnimationComplete,
    taglines.length,
    currentTaglineIndex,
    firstCycleComplete,
  ]);

  return (
    <div className="container mx-auto max-width section">
      {/* Use a grid layout with fixed positions for both sections */}
      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* Left section for text - with minimum height to prevent layout shifts */}
        <div className="min-h-[250px] md:min-h-[300px] xl:min-h-[350px]">
          <h1 className="text-2xl text-dark-heading dark:text-light-heading md:text-4xl xl:text-5xl xl:leading-tight font-bold">
            {greetingText}
          </h1>
          <h1 className="text-2xl text-dark-heading dark:text-light-heading md:text-4xl xl:text-5xl xl:leading-tight font-bold mt-2">
            {introText}
          </h1>

          {/* Name with fade-in sparkle effect */}
          <h1
            className={`text-2xl md:text-4xl xl:text-5xl xl:leading-tight font-bold mt-2 bg-gradient-to-r from-sky-500 via-purple-500 to-pink-600 bg-clip-text text-transparent ${showName ? "name-sparkle-in" : "opacity-0"}`}
          >
            {name}
            {showName && (
              <span className="sparkle-container">
                <span className="sparkle"></span>
                <span className="sparkle"></span>
                <span className="sparkle"></span>
              </span>
            )}
          </h1>

          <h2 className="text-2xl text-dark-heading dark:text-light-heading md:text-4xl xl:text-5xl xl:leading-tight font-bold mt-2">
            <span className="inline-block whitespace-nowrap">
              <span
                className={`inline-block transition-opacity duration-500 ${showImA ? "opacity-100" : "opacity-0"}`}
              >
                I'm a
              </span>
              {showTagline &&
                (!firstCycleComplete ? (
                  <span key={blurKey} className="blur-morph-text ml-2">
                    {taglines[currentTaglineIndex]}
                  </span>
                ) : (
                  <span
                    key={glitchKey}
                    className="glitch-text ml-2"
                    data-text={taglines[currentTaglineIndex]}
                  >
                    {taglines[currentTaglineIndex]}
                  </span>
                ))}
            </span>
          </h2>
        </div>

        {/* Right section for logo - fixed position */}
        <div className="md:mt-0 flex justify-center md:justify-end items-center absolute md:relative top-[300px] right-0 md:top-auto md:right-auto">
          <img className="w-1/2 md:ml-auto" src={img} alt="Robert O'Connor" />
        </div>
      </div>
    </div>
  );
}

export default Home;
